import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { TID } from "@/constants/tids";

const inp =
  "bg-white border border-gray-200 rounded-xl h-12 px-3.5 w-full focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] text-sm text-[#0A0A0A] placeholder:text-gray-400 outline-none transition-colors";
const lbl = "block text-xs font-bold uppercase tracking-[0.14em] text-gray-500 mb-1.5";

export default function AdminSettings() {
  const { settings, setSettings } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude !== "" && form.latitude != null ? parseFloat(form.latitude) : null,
        longitude: form.longitude !== "" && form.longitude != null ? parseFloat(form.longitude) : null,
      };
      const { data } = await api.put("/settings", payload);
      setSettings(data);
      toast.success("Setări salvate");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Configurare</div>
        <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A0A0A]">Setări</h1>
        <p className="mt-1 text-sm text-gray-500">Actualizează datele companiei și configurările site-ului.</p>
      </div>

      <form onSubmit={save} className="space-y-4 sm:space-y-6 max-w-4xl pb-24 lg:pb-0">
        <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 md:p-8">
          <h2 className="text-base sm:text-lg font-bold text-[#0A0A0A] mb-5 sm:mb-6">Companie</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Nume companie</label>
              <input className={inp} value={form.company_name || ""} onChange={set("company_name")} />
            </div>
            <div>
              <label className={lbl}>Telefon</label>
              <input type="tel" inputMode="tel" className={inp} value={form.phone || ""} onChange={set("phone")} />
            </div>
            <div>
              <label className={lbl}>Email</label>
              <input type="email" inputMode="email" className={inp} value={form.email || ""} onChange={set("email")} />
            </div>
            <div>
              <label className={lbl}>WhatsApp</label>
              <input type="tel" inputMode="tel" className={inp} value={form.whatsapp || ""} onChange={set("whatsapp")} />
            </div>
            <div>
              <label className={lbl}>Facebook URL</label>
              <input inputMode="url" className={inp} value={form.facebook_url || ""} onChange={set("facebook_url")} />
            </div>
            <div>
              <label className={lbl}>Logo URL</label>
              <input inputMode="url" className={inp} value={form.logo_url || ""} onChange={set("logo_url")} />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 md:p-8">
          <h2 className="text-base sm:text-lg font-bold text-[#0A0A0A] mb-5 sm:mb-6">Adresă & program</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className={lbl}>Stradă</label>
              <input className={inp} value={form.address_street || ""} onChange={set("address_street")} />
            </div>
            <div>
              <label className={lbl}>Oraș</label>
              <input className={inp} value={form.address_city || ""} onChange={set("address_city")} />
            </div>
            <div className="sm:col-span-2">
              <label className={lbl}>Țară</label>
              <input className={inp} value={form.address_country || ""} onChange={set("address_country")} />
            </div>
            <div className="sm:col-span-3">
              <label className={lbl}>Program (separator &quot; · &quot; între zile)</label>
              <input className={inp} value={form.schedule || ""} onChange={set("schedule")} />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 md:p-8">
          <h2 className="text-base sm:text-lg font-bold text-[#0A0A0A] mb-1">Coordonate hartă</h2>
          <p className="text-xs text-gray-500 mb-5 sm:mb-6">
            Latitudinea și longitudinea folosite pentru harta din pagina Contact. Poți obține
            coordonatele exacte făcând click-dreapta pe locație în Google Maps.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Latitudine</label>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                className={inp}
                value={form.latitude ?? ""}
                onChange={set("latitude")}
                placeholder="47.6435"
              />
            </div>
            <div>
              <label className={lbl}>Longitudine</label>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                className={inp}
                value={form.longitude ?? ""}
                onChange={set("longitude")}
                placeholder="26.2411"
              />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 md:p-8">
          <h2 className="text-base sm:text-lg font-bold text-[#0A0A0A] mb-5 sm:mb-6">SEO</h2>
          <div className="space-y-4">
            <div>
              <label className={lbl}>Titlu SEO</label>
              <input className={inp} value={form.seo_title || ""} onChange={set("seo_title")} />
            </div>
            <div>
              <label className={lbl}>Descriere SEO</label>
              <textarea
                className={`${inp} h-24 py-3 resize-none`}
                value={form.seo_description || ""}
                onChange={set("seo_description")}
              />
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <button
            data-testid={TID.adminSettingsSave}
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto h-12 px-8 rounded-full bg-[#0A0A0A] hover:bg-[#222] active:bg-black disabled:opacity-60 text-white text-sm font-semibold transition-colors"
          >
            {saving ? "Se salvează..." : "Salvează setări"}
          </button>
        </div>
      </form>
    </div>
  );
}
